import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { from, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/auth/auth';
import { CompetitionsService, Pick } from '../../../core/competitions/competitions';
import { PeopleService, PersonSearchResult } from '../../../core/people/people';
import { PickList } from '../pick-list/pick-list';

/** F-029 — as-you-type search against the local catalog only, mirroring
 *  competition-picks.ts/admin-people-list.ts. Keep these two values in
 *  sync with every other celebrity search UI. */
const LIVE_SEARCH_DEBOUNCE_MS = 200;
const LIVE_SEARCH_MIN_LENGTH = 4;

/** F-032 — one input name's local-catalog lookup result, for the bulk
 *  add-picks panel below. Unlike bulk-add-people-modal.ts's RowViewModel,
 *  there's no Wikipedia visible/hidden split — this only ever searches the
 *  local catalog (the whole point is these people are already catalogued),
 *  so every match is a real, already-vetted person. */
interface BulkPickRow {
  name: string;
  matches: PersonSearchResult[];
  status: 'pending' | 'adding' | 'added' | 'error';
  errorMessage?: string;
}

/** Splits on commas, trims, drops empties, dedupes case-insensitively —
 *  copied from bulk-add-people-modal.ts (kept local rather than shared
 *  since it's five lines and the two features otherwise don't interact). */
function parseNames(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const name = part.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** A contestant's picks for one specific competition, in a modal.
 *  Mounted/unmounted by the parent (via @if) — fetches on init rather than
 *  reacting to input changes, since a new instance is created each time
 *  it's opened for a different contestant (F-004 Slice 7).
 *
 *  F-028 — a site admin additionally gets an add/remove-pick UI here, for
 *  recreating a contestant's historical entries (including already-DEAD
 *  people — search always passes includeDead: true, and the backend
 *  transparently allows a DEAD pick when the caller is genuinely
 *  SITE_ADMIN). Everyone else still gets the original read-only view.
 *  F-029 — as-you-type live search added, same debounced local-catalog
 *  pattern as the real picks page; the explicit Search button still runs
 *  the full local-then-Wikipedia-fallback search via search() below. */
@Component({
  selector: 'app-entries-modal',
  imports: [PickList, ReactiveFormsModule, NgTemplateOutlet],
  templateUrl: './entries-modal.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './entries-modal.css',
})
export class EntriesModal implements OnInit {
  private readonly competitions = inject(CompetitionsService);
  private readonly people = inject(PeopleService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly competitionId = input.required<number>();
  readonly userId = input.required<number>();
  readonly displayName = input('');
  /** F-028 — for regular/alternate cap tracking in the add-pick UI. The
   *  parent (competition-detail) already has the Competition loaded, so
   *  no extra fetch here; unused when the viewer isn't a site admin. */
  readonly maxEntries = input<number | null>(null);
  readonly maxAlternates = input<number | null>(null);
  readonly closed = output<void>();

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly picks = signal<Pick[]>([]);

  readonly isSiteAdmin = computed(() => this.auth.user()?.siteRole === 'SITE_ADMIN');

  readonly searchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });
  readonly searching = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly searchResults = signal<PersonSearchResult[]>([]);
  readonly noResultsFound = signal(false);
  /** Reveals hiddenResults() below — reset on every new search. */
  readonly showHidden = signal(false);
  /** F-031 — true once the current query's results already reflect a
   *  Wikipedia check (see competition-picks.ts for the full rationale). */
  readonly wikipediaAlsoSearched = signal(false);
  /** Tracks which specific (result, bucket) add is in flight. */
  readonly addingKey = signal<string | null>(null);
  readonly removingId = signal<number | null>(null);

  /** F-032 — bulk add-picks-by-name panel, independent of the single
   *  search box above. Local-catalog-only (no Wikipedia): the whole point
   *  is speeding up entry of people already added to the catalog, not
   *  discovering new ones. */
  readonly showBulkAdd = signal(false);
  readonly bulkNamesForm = new FormGroup({
    names: new FormControl('', { nonNullable: true }),
  });
  readonly bulkLookingUp = signal(false);
  readonly bulkLookupError = signal<string | null>(null);
  readonly bulkRows = signal<BulkPickRow[]>([]);

  /** Same noise filter as the real picks page — Wikipedia search results
   *  include non-person pages (a celebrity's movies/discography) with no
   *  confirmed birth date; local catalog hits are already-vetted. */
  readonly visibleResults = computed(() =>
    this.searchResults().filter((r) => r.source === 'local' || r.dateOfBirth !== null),
  );
  readonly hiddenResults = computed(() =>
    this.searchResults().filter((r) => r.source === 'wikipedia' && r.dateOfBirth === null),
  );

  readonly regularCount = computed(() => this.picks().filter((p) => !p.isAlternate).length);
  readonly alternateCount = computed(() => this.picks().filter((p) => p.isAlternate).length);
  readonly regularCapReached = computed(() => {
    const max = this.maxEntries();
    return max !== null && this.regularCount() >= max;
  });
  readonly alternateCapReached = computed(() => {
    const max = this.maxAlternates();
    return max !== null && this.alternateCount() >= max;
  });

  async ngOnInit(): Promise<void> {
    this.wireLiveLocalSearch();
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      this.picks.set(
        await this.competitions.listPicksForContestant(
          this.competitionId(),
          this.userId(),
        ),
      );
    } catch {
      this.errorMessage.set(
        "Could not load this contestant's picks — try again.",
      );
    } finally {
      this.loading.set(false);
    }
  }

  /** F-029 — searches the local catalog only (never Wikipedia) as the user
   *  types, once they've paused and typed enough characters. Always
   *  includeDead: true, matching this modal's whole purpose. Reuses the
   *  same searchResults/searchForm the explicit search() below populates.
   *  Guarded on isSiteAdmin() too — the search box only ever renders for a
   *  site admin, but this instance exists (and ngOnInit runs) for every
   *  viewer, so the guard keeps a non-admin from ever triggering a call. */
  private wireLiveLocalSearch(): void {
    this.searchForm.controls.q.valueChanges
      .pipe(
        debounceTime(LIVE_SEARCH_DEBOUNCE_MS),
        map((q) => q.trim()),
        distinctUntilChanged(),
        switchMap((q) => {
          if (!this.isSiteAdmin() || q.length < LIVE_SEARCH_MIN_LENGTH) {
            return of<PersonSearchResult[]>([]);
          }
          this.searching.set(true);
          this.searchError.set(null);
          return from(this.people.search(q, 10, true, true)).pipe(
            catchError(() => {
              this.searchError.set('Search failed — try again in a moment.');
              return of<PersonSearchResult[]>([]);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((results) => {
        this.searchResults.set(results);
        this.showHidden.set(false);
        this.noResultsFound.set(false);
        this.wikipediaAlsoSearched.set(false);
        this.searching.set(false);
      });
  }

  close(): void {
    this.closed.emit();
  }

  resultKey(result: PersonSearchResult, isAlternate: boolean): string {
    return `${result.wikipediaUrl ?? result.name}-${isAlternate}`;
  }

  /** Always includeDead: true — the whole point of this flow is picking
   *  people who've already died since the competition started. F-031 —
   *  includeWikipedia (via the "Search Wikipedia anyway" button) also
   *  checks Wikipedia even when the catalog already has hits. */
  async search(includeWikipedia = false): Promise<void> {
    const q = this.searchForm.getRawValue().q.trim();
    if (!q) return;

    this.searching.set(true);
    this.searchError.set(null);
    this.noResultsFound.set(false);
    try {
      const results = await this.people.search(q, 10, false, true, includeWikipedia);
      this.searchResults.set(results);
      this.showHidden.set(false);
      this.noResultsFound.set(results.length === 0);
      this.wikipediaAlsoSearched.set(
        includeWikipedia || !results.some((r) => r.source === 'local'),
      );
    } catch {
      this.searchError.set('Search failed — try again in a moment.');
    } finally {
      this.searching.set(false);
    }
  }

  toggleBulkAdd(): void {
    this.showBulkAdd.update((v) => !v);
  }

  bulkResultKey(result: PersonSearchResult): string {
    return result.wikipediaUrl ?? `local-${result.id}`;
  }

  /** F-032 — one local-catalog search per pasted name, in parallel. Unlike
   *  bulk-add-people-modal.ts's Wikipedia lookup (chunked with a progress
   *  bar to respect an external rate limit), these are plain local DB
   *  queries — fast enough, and nothing external to throttle, so a single
   *  Promise.all and a plain spinner is enough. */
  async bulkLookUp(): Promise<void> {
    const names = parseNames(this.bulkNamesForm.getRawValue().names);
    if (names.length === 0) return;

    this.bulkLookingUp.set(true);
    this.bulkLookupError.set(null);
    try {
      const results = await Promise.all(
        names.map((name) => this.people.search(name, 10, true, true)),
      );
      this.bulkRows.set(
        names.map((name, i) => ({
          name,
          matches: results[i],
          status: 'pending' as const,
        })),
      );
    } catch {
      this.bulkLookupError.set('Lookup failed — try again in a moment.');
    } finally {
      this.bulkLookingUp.set(false);
    }
  }

  /** A local search result always has an id (it's already in the catalog),
   *  so this skips addPick()'s Wikipedia-candidate select() step entirely. */
  async addBulkPick(
    row: BulkPickRow,
    result: PersonSearchResult,
    isAlternate: boolean,
  ): Promise<void> {
    if (result.id === null) return;
    this.updateBulkRow(row.name, { status: 'adding' });
    try {
      const pick = await this.competitions.addPickForContestant(
        this.competitionId(),
        this.userId(),
        { personId: result.id, isAlternate },
      );
      this.picks.update((current) => [...current, pick]);
      this.updateBulkRow(row.name, { status: 'added' });
    } catch (err) {
      this.updateBulkRow(row.name, {
        status: 'error',
        errorMessage: this.addErrorMessage(err),
      });
    }
  }

  private updateBulkRow(name: string, patch: Partial<BulkPickRow>): void {
    this.bulkRows.update((rows) =>
      rows.map((r) => (r.name === name ? { ...r, ...patch } : r)),
    );
  }

  async addPick(result: PersonSearchResult, isAlternate: boolean): Promise<void> {
    const key = this.resultKey(result, isAlternate);
    this.addingKey.set(key);
    this.searchError.set(null);
    try {
      const personId =
        result.id ??
        (
          await this.people.select({
            name: result.name,
            knownFor: result.knownFor ?? undefined,
            dateOfBirth: result.dateOfBirth ?? undefined,
            dateOfDeath: result.dateOfDeath ?? undefined,
            causeOfDeath: result.causeOfDeath ?? undefined,
            wikipediaUrl: result.wikipediaUrl ?? undefined,
            imageUrl: result.imageUrl ?? undefined,
            aliases: result.aliases,
          })
        ).id;

      const pick = await this.competitions.addPickForContestant(
        this.competitionId(),
        this.userId(),
        { personId, isAlternate },
      );
      this.picks.update((current) => [...current, pick]);
      this.searchResults.set([]);
      this.searchForm.controls.q.setValue('');
    } catch (err) {
      this.searchError.set(this.addErrorMessage(err));
    } finally {
      this.addingKey.set(null);
    }
  }

  async removePick(pick: Pick): Promise<void> {
    this.removingId.set(pick.id);
    this.searchError.set(null);
    try {
      await this.competitions.removePickForContestant(
        this.competitionId(),
        this.userId(),
        pick.id,
      );
      this.picks.update((current) => current.filter((p) => p.id !== pick.id));
    } catch {
      this.searchError.set('Could not remove that pick — try again.');
    } finally {
      this.removingId.set(null);
    }
  }

  private addErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 409) {
        return 'Already picked, or the cap for that bucket is reached.';
      }
      if (err.status === 403) {
        return 'This competition is on hold until its owner resolves it.';
      }
      if (err.status === 404) {
        return 'That person could not be found — try searching again.';
      }
    }
    return 'Something went wrong — try again in a moment.';
  }
}
